import { isEqual } from 'lodash';
import {
  EnhancedPlan,
  PlanTypeBE,
  PlanSubtypes,
  AddonSettingsType,
  AddonSettingsKeyType,
  planSubtypesByType,
} from '../interfaces/subscription';
import CouponModel from './CouponModel';

export type addOnDowngrades = {
  userSeatsDowngrade: number;
  allotmentLevelDowngrade: number;
};

export type AddonInfo = {
  quantity: number;
  pricePerUnit: number;
  priceTotal: number;
  included?: number;
};

export type Invoice = {
  // new billing 2023 plans have fixedAddOns, old plans have addOns
  allAddOns?: {
    [key in
      | keyof AddonSettingsKeyType
      | 'userSeats'
      | 'sendAllotment'
      | 'additionalPages'
      | 'smsNumber']: AddonInfo;
  };
  addOnsPrice: number;
  fixedAddOns?: {
    userSeats?: AddonInfo;
    additionalPages?: AddonInfo;
    smsNumber?: AddonInfo;
  };
  fixedAddOnsPrice: number;
  subsPrice: number;
  sum: number;
};

export type AddonUsageValueType = {
  used: number | null;
  cost: string | null;
  included: number | null;
};

export type AddonUsageType = {
  [key in keyof AddonSettingsKeyType]: AddonUsageValueType;
};

export type SubscriptionModel = {
  id: number;
  activatedAt: string;
  addonSettings: AddonSettingsType;
  addonUsage: AddonUsageType;
  billingPeriodLength: 'monthly' | 'annually';
  planIntervalLength: number | null;
  canceledAt: string;
  expiresAt: string;
  teamDowngradePending: boolean;
  coupon?: CouponModel;
  endsAt: string;
  state: 'in_trial' | 'active' | 'canceled' | 'future' | 'expired' | 'pending'; // 'pending' shopify only
  active: boolean;
  priceFactor: number;
  planCode: string;
  extraPages: number | null;
  pagesLimit: number | null;
  planType: PlanTypeBE;
  planSubtypes: PlanSubtypes;
  extraPageAddOnPrice?: number;
  downgradePending?: boolean;
  overagesEnabled: boolean;
  overageFee?: number;
  overageCalls?: number;
  readonlyOverages?: boolean;
  sendAllotmentAddOnPrice: number;
  unitAmountInCents: number;
  maxSendAllotment: number;
  sendAllotmentLevel: number;
  landmineOpenId?: number;
  userSeats: number | null;
  userSeatsUsed: number;
  addOnDowngrades: addOnDowngrades;
  smsNumberAddOnPrice: number;
  extraUserSeats: number;
  userSeatAddOnPrice: number;
  smsNumbers: {
    [key: number]: number;
  };
  currentInvoiceAmount?: Invoice;
  nextInvoiceAmount?: Invoice;
  basePrice: number;
  calendlyLink: string;
  csRepFullName: string;
  failedPayment?: boolean;
  xrayLimit: number | null;
  budgetCap: number | null;
};

export function isBilling2023Plan(plan: EnhancedPlan) {
  return plan.planSubtypes.includes(PlanSubtypes.billing2023);
}

export function isBilling2023V2Plan(plan: EnhancedPlan) {
  return plan.planSubtypes.includes(PlanSubtypes.billing2023v2);
}

export function isBilling2023Subscription(subscription: SubscriptionModel) {
  return subscription?.planSubtypes?.includes(PlanSubtypes.billing2023);
}

export function isBilling2023V2Subscription(subscription: SubscriptionModel) {
  return subscription?.planSubtypes?.includes(PlanSubtypes.billing2023v2);
}

export function isBilling2025Subscription(subscription: SubscriptionModel) {
  return subscription?.planSubtypes?.includes(PlanSubtypes.billing2025);
}

export function isBilling2025V2Subscription(subscription: SubscriptionModel) {
  return subscription?.planSubtypes?.includes(PlanSubtypes.billing2025v2);
}

export function isNewAgencyPlan(plan: EnhancedPlan) {
  return [
    planSubtypesByType.agencyLevel1,
    planSubtypesByType.agencyLevel2,
    planSubtypesByType.agencyLevel3,
  ].some(subtypes => isEqual(plan.planSubtypes, subtypes));
}
